<template>
  <q-dialog ref="dialogRef" class="modal" persistent @hide="onDialogHide">
    <q-card
      class="q-dialog-plugin tw-flex tw-max-h-[75vh] tw-w-full tw-flex-col !tw-shadow-xl"
      style="max-width: 1080px"
    >
      <q-card-section
        class="tw-flex tw-items-center tw-justify-between tw-gap-4 !tw-px-4 tw-pb-0"
      >
        <h3 class="tw-m-0 tw-text-lg">FAQ-vragen sorteren</h3>
        <c-button-icon
          title="Sluiten"
          flat
          icon="fas fa-times"
          @click="onDialogCancel"
        />
      </q-card-section>

      <q-card-section class="tw-flex-1 tw-overflow-auto !tw-px-4 tw-pt-4">
        <div class="tw-pb-4">
          Versleep een vraag om de volgorde te veranderen. Je kan ook een vraag
          naar een andere categorie slepen. Versleep categorieën om hun volgorde
          te wijzigen.
        </div>
        <div
          class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3"
        >
          <div
            v-for="(cat, catIndex) in localCategories"
            :key="cat.uuid"
            class="tw-relative tw-rounded tw-border tw-bg-white tw-p-3 tw-transition-all"
            :class="{
              'tw-border-gray-200': categoryHover !== catIndex,
              'tw-border-2 tw-border-primary': categoryHover === catIndex,
            }"
            @dragover.prevent="(e) => onCategoryDragOver(e, catIndex)"
            @dragleave="onCategoryDragLeave"
            @drop="(e) => onCategoryDrop(e, catIndex)"
          >
            <div
              v-if="
                categoryHover === catIndex && draggingCategory.index < catIndex
              "
              class="tw-absolute tw--left-2 tw-bottom-0 tw-top-0 tw-w-1 tw-rounded tw-bg-primary"
            ></div>
            <div
              v-if="
                categoryHover === catIndex && draggingCategory.index > catIndex
              "
              class="tw-absolute tw--right-2 tw-bottom-0 tw-top-0 tw-w-1 tw-rounded tw-bg-primary"
            ></div>

            <div
              class="tw-mb-2 tw-flex tw-cursor-move tw-items-center tw-justify-between"
              draggable="true"
              @dragstart="(e) => onCategoryDragStart(e, catIndex)"
              @dragend="onCategoryDragEnd"
            >
              <div class="tw-flex tw-min-w-0 tw-flex-col">
                <div
                  v-if="parentTitleFor(cat) !== null"
                  class="body-small tw-truncate tw-text-gray-500"
                >
                  ↳ subcategorie van {{ parentTitleFor(cat) }}
                </div>
                <div class="tw-flex tw-items-center tw-gap-2">
                  <q-icon
                    :name="toClassicIcon(dragIcon, 'solid')"
                    size="12px"
                    class="tw-cursor-move tw-text-gray-500"
                  />
                  <div class="tw-truncate tw-font-semibold">
                    {{ cat.title }}
                  </div>
                </div>
              </div>
              <div class="body-small tw-shrink-0 tw-text-gray-500">
                {{ cat.items.length }} vragen
              </div>
            </div>

            <q-list
              dense
              separator
              class="tw-rounded tw-border tw-border-gray-100"
              @dragover.prevent.stop="(e) => onDragOverCategoryEnd(e, cat.uuid)"
              @dragleave.stop="(e) => onDragLeaveList(e, cat.uuid)"
              @drop.stop="(e) => onDropOnCategory(e, cat.uuid)"
            >
              <template v-for="(it, index) in cat.items" :key="it.uuid">
                <div
                  v-if="hover.categoryId === cat.uuid && hover.index === index"
                  class="tw-mx-2 tw-h-[2px] tw-rounded tw-bg-primary"
                ></div>
                <q-item
                  class="tw-flex tw-items-center tw-gap-2 tw-bg-white"
                  clickable
                  draggable="true"
                  @dragstart="(e) => onDragStart(e, it.uuid, cat.uuid, index)"
                  @dragover.prevent.stop="
                    (e) => onDragOverItem(e, cat.uuid, index)
                  "
                  @drop.stop="(e) => onDropOnItem(e, cat.uuid, index)"
                >
                  <q-item-section
                    avatar
                    class="tw-w-5 tw-basis-5 tw-text-gray-500"
                  >
                    <q-icon
                      :name="toClassicIcon(dragIcon, 'solid')"
                      size="12px"
                    />
                  </q-item-section>
                  <q-item-section>
                    <div
                      class="tw-flex tw-items-center tw-justify-between tw-gap-3"
                    >
                      <div class="tw-overflow-hidden tw-truncate">
                        {{ it.title }}
                      </div>
                      <c-badge
                        v-if="it.is_published === false"
                        label="concept"
                      />
                    </div>
                  </q-item-section>
                  <q-item-section side class="body-small tw-text-gray-400">
                    #{{ index + 1 }}
                  </q-item-section>
                </q-item>
              </template>

              <div
                class="tw-py-2 tw-text-center tw-text-xs tw-text-gray-400"
                @dragover.prevent.stop="
                  (e) => onDragOverCategoryEnd(e, cat.uuid)
                "
                @drop.stop="(e) => onDropOnCategory(e, cat.uuid)"
              >
                <div
                  v-if="
                    hover.categoryId === cat.uuid &&
                    hover.index === cat.items.length
                  "
                  class="tw-mx-2 tw-mb-2 tw-h-[2px] tw-rounded tw-bg-primary"
                ></div>
                Sleep hierheen om onderaan te plaatsen
              </div>
            </q-list>
          </div>
        </div>
      </q-card-section>

      <q-card-actions class="!tw-px-4 tw-pb-4 tw-pt-0" align="right">
        <c-button
          color="transparent"
          text-color="gray"
          label="Annuleren"
          @click="onDialogCancel"
        />
        <c-button
          :disable="saving"
          color="primary"
          :label="saving ? 'Opslaan…' : 'Opslaan'"
          @click="onSave"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { dragIcon, toClassicIcon } from "common/defaultIcons";
import type { FaqCategoryWithItems, FaqItem } from "models/knowledge-base/faq";
import { useDialogPluginComponent } from "quasar";
import { useFaqStore } from "store/modules/faqStore";
import { reactive, ref } from "vue";

interface Props {
  categories: FaqCategoryWithItems[];
}

const props = defineProps<Props>();
defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

const faqStore = useFaqStore();

const localCategories = ref<FaqCategoryWithItems[]>(
  JSON.parse(JSON.stringify(props.categories || [])) as FaqCategoryWithItems[],
);

const parentTitleFor = (cat: FaqCategoryWithItems): string | null => {
  if (cat.parent_uuid === null) {
    return null;
  }
  return (
    localCategories.value.find((c) => c.uuid === cat.parent_uuid)?.title ?? null
  );
};

const dragging = reactive<{
  uuid: string | null;
  categoryId: string | null;
  index: number;
}>({ uuid: null, categoryId: null, index: -1 });

const draggingCategory = reactive<{ index: number }>({ index: -1 });
const categoryHover = ref<number>(-1);
const saving = ref(false);

const onDragStart = (
  e: DragEvent,
  uuid: string,
  categoryId: string,
  index: number,
) => {
  dragging.uuid = uuid;
  dragging.categoryId = categoryId;
  dragging.index = index;
  e.dataTransfer?.setData("text/plain", uuid);
  e.dataTransfer!.effectAllowed = "move";
  clearHover();
  draggingCategory.index = -1;
  categoryHover.value = -1;
};

const normalizeOrders = () => {
  localCategories.value.forEach((c) => {
    c.items = (c.items || []).map((it, idx) => ({
      ...it,
      order: idx,
    })) as FaqItem[];
  });
};

const removeFromSource = (): { item: FaqItem | null } => {
  if (!dragging.uuid || !dragging.categoryId) {
    return { item: null };
  }
  const sourceCat = localCategories.value.find(
    (c) => c.uuid === dragging.categoryId,
  );
  if (!sourceCat) {
    return { item: null };
  }
  const [removed] = sourceCat.items.splice(dragging.index, 1);
  normalizeOrders();
  return { item: removed ?? null };
};

const onDropOnItem = (
  e: DragEvent,
  targetCategoryId: string,
  targetIndex: number,
) => {
  e.preventDefault();
  const { item } = removeFromSource();
  if (!item) {
    return;
  }
  const targetCat = localCategories.value.find(
    (c) => c.uuid === targetCategoryId,
  );
  if (!targetCat) {
    return;
  }
  const insertIndex =
    targetCategoryId === dragging.categoryId && targetIndex > dragging.index
      ? targetIndex - 1
      : targetIndex;
  targetCat.items.splice(insertIndex, 0, item);
  normalizeOrders();
  clearHover();
};

const onDropOnCategory = (e: DragEvent, targetCategoryId: string) => {
  e.preventDefault();
  const { item } = removeFromSource();
  if (!item) {
    return;
  }
  const targetCat = localCategories.value.find(
    (c) => c.uuid === targetCategoryId,
  );
  if (!targetCat) {
    return;
  }
  targetCat.items.push(item);
  normalizeOrders();
  clearHover();
};

const hover = reactive<{ categoryId: string | null; index: number }>({
  categoryId: null,
  index: -1,
});
const setHover = (categoryId: string, index: number) => {
  hover.categoryId = categoryId;
  hover.index = index;
};
const clearHover = () => setHover(null as unknown as string, -1);

const onDragOverItem = (
  e: DragEvent,
  targetCategoryId: string,
  targetIndex: number,
) => {
  e.preventDefault();
  setHover(targetCategoryId, targetIndex);
};

const onDragOverCategoryEnd = (e: DragEvent, targetCategoryId: string) => {
  e.preventDefault();
  const cat = localCategories.value.find((c) => c.uuid === targetCategoryId);
  const endIndex = cat?.items?.length ?? 0;
  setHover(targetCategoryId, endIndex);
};

const onDragLeaveList = (_e: DragEvent, _categoryId: string) => {
  clearHover();
};

const onCategoryDragStart = (e: DragEvent, index: number) => {
  draggingCategory.index = index;
  e.dataTransfer!.effectAllowed = "move";
  e.dataTransfer!.setData("text/plain", `category-${index}`);
  dragging.uuid = null;
  dragging.categoryId = null;
  dragging.index = -1;
  clearHover();
};

const onCategoryDragOver = (e: DragEvent, targetIndex: number) => {
  if (draggingCategory.index === -1) {
    return;
  }
  if (dragging.uuid !== null) {
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  if (draggingCategory.index === targetIndex) {
    categoryHover.value = -1;
    return;
  }
  categoryHover.value = targetIndex;
};

const onCategoryDragLeave = () => {
  categoryHover.value = -1;
};

const onCategoryDragEnd = () => {
  draggingCategory.index = -1;
  categoryHover.value = -1;
};

const onCategoryDrop = (e: DragEvent, targetIndex: number) => {
  if (draggingCategory.index === -1) {
    return;
  }
  if (dragging.uuid !== null) {
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  if (draggingCategory.index === targetIndex) {
    draggingCategory.index = -1;
    categoryHover.value = -1;
    return;
  }
  const [movedCategory] = localCategories.value.splice(
    draggingCategory.index,
    1,
  );
  localCategories.value.splice(targetIndex, 0, movedCategory);
  localCategories.value.forEach((cat, idx) => {
    cat.order = idx;
  });
  draggingCategory.index = -1;
  categoryHover.value = -1;
};

const onSave = async () => {
  const grouped = localCategories.value.map((c, idx) => ({
    category_uuid: c.uuid,
    order: idx,
    items: (c.items || []).map((i, itemIdx) => ({
      uuid: i.uuid,
      order: itemIdx,
    })),
  }));
  try {
    saving.value = true;
    await faqStore.reorder(grouped);
    onDialogOK();
  } finally {
    saving.value = false;
  }
};
</script>
